
import { useCallback, useEffect, useState } from 'react';
import { adminHttpClient } from '@/admin/lib/admin-http-client';
import { getAdminEndpointUrl } from '@/admin/lib/admin-api-config';

interface AccountRow {
  codigoComercio: string;
  rutTitular: string;
  numeroCuenta: string;
  banco: string;
  tipoCuenta: string;
  isVigente: boolean;
  fechaActualizacion: string;
}

const formatDate = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat('es-CL', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'America/Santiago' }).format(new Date(iso));
  } catch {
    return iso;
  }
};

/**
 * Tabla read-only de cuentas TBK detectadas por el scrapper para un comercio.
 *
 * El tracking de PAC externo se removió del panel: PAC queda fuera del alcance actual de la
 * app y se revisará después con Lups (decisión meeting 24-Abr-2026). Por ahora la tabla es
 * puramente informativa para que el admin pueda saber qué cuentas existen.
 */
export default function MerchantAccountsPanel({ companyId }: { companyId: string }) {
  const [rows, setRows] = useState<AccountRow[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminHttpClient.get<AccountRow[]>(getAdminEndpointUrl('merchantAccounts', 'list', { companyId }));
      setRows(data);
    } catch (err) {
      console.error('Error cargando cuentas', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <section className="rounded-xl border bg-card p-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold">Cuentas detectadas (TBK)</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Cuentas de abono scrapeadas desde TBK por código de comercio. Vista read-only.
          </p>
        </div>
      </div>

      {loading && <p className="mt-4 text-sm text-muted-foreground">Cargando cuentas…</p>}

      {!loading && rows.length === 0 && (
        <p className="mt-4 text-sm text-muted-foreground">El scrapper aún no detectó cuentas para este comercio.</p>
      )}

      {!loading && rows.length > 0 && (
        <div className="mt-4 overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="py-2 pr-3">Código</th>
                <th className="py-2 pr-3">Banco / Cuenta</th>
                <th className="py-2 pr-3">RUT titular</th>
                <th className="py-2 pr-3">Tipo</th>
                <th className="py-2 pr-3">Vigente</th>
                <th className="py-2 pr-3">Actualizada</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.codigoComercio}-${row.rutTitular}-${row.numeroCuenta}`} className={`border-t border-border ${row.isVigente ? '' : 'opacity-60'}`}>
                  <td className="py-2 pr-3 font-mono text-xs">{row.codigoComercio}</td>
                  <td className="py-2 pr-3">
                    <div className="font-medium">{row.banco}</div>
                    <div className="text-xs text-muted-foreground font-mono">{row.numeroCuenta}</div>
                  </td>
                  <td className="py-2 pr-3 text-xs font-mono">{row.rutTitular}</td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{row.tipoCuenta}</td>
                  <td className="py-2 pr-3">
                    {row.isVigente ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Sí</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-400">No</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground">{formatDate(row.fechaActualizacion)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
